import { useEffect, useRef } from 'react'
import { ConeTecMark } from '../icons'
import { useOpenLanes } from '../../lib/openLanes'
import { HelpDoor, OneMessage, RequestLink, Thumbs } from '../AskOneDrawer'
import { BoardStep, RouteSent, useRouting } from '../SupportSend'
import type { OneAnswer } from '../../lib/wiki'
import { useHubLang } from '../../lib/i18n'
import { ASK } from '../../lib/hubCopy'

/**
 * Ask One on #/wiki — David, 22 Sep: "go back to Ask One first and then the
 * path to post the question."
 *
 * ── NOTHING IN THIS FILE IS A COPY ──────────────────────────────────────────
 *
 * WIK-19 took the ask box off this screen because a second Ask One surface
 * meant a second place to keep the machine-authorship disclosure, the rating
 * and the public-name warning correct. The box is back; the second place is
 * not. The answer, the thumbs, the Help door and the idea/fault link render
 * from AskOneDrawer.tsx; the board form and its send are SupportSend.tsx's
 * `BoardStep` and `useRouting`. If something here starts to need its own
 * wording for any of them, the wording belongs in those files.
 *
 * ── S2 (31 Oct, HLP-44): NO ROUTING STEP, ONE BOARD BUTTON ─────────────────
 *
 * The thumbs-down no longer opens a list of destinations here, and an idea or
 * a fault is a link to Help, exactly as in the panel: Ask One sends nothing.
 * The page's own "Post to the board" button is the one door that still opens
 * from an answer, because the community board IS Wiki One - a public post,
 * not help - and v2 keeps it on this page only.
 *
 * ── IT IS A CHILD OF THE SHELL, AND THAT IS NOT INCIDENTAL ──────────────────
 *
 * `useOpenLanes()` reads a context AppShell provides. A route renders the
 * shell from its own body, so a hook called in the ROUTE sits above the
 * provider and silently gets the default — which is precisely how the old Ask
 * One button on this page did nothing for five days. This component is
 * rendered as the shell's child, so it is inside.
 *
 * ── ONE TURN, NOT A CONVERSATION ────────────────────────────────────────────
 *
 * The page answers the question in the box. A follow-up is the drawer's job —
 * a conversation on a knowledge-base screen is two conversations on one site.
 * So each ask is a fresh `key` from the route, which remounts this and resets
 * the routing: a door chosen for the last question must not carry to the next.
 */
export function AskOnePanel({
  question,
  answer,
  asking,
  postRequest = 0,
  onPosted,
  onAsk,
  onEdit,
}: {
  /** v9. Ask a clarifying option as the new question. */
  onAsk?: (question: string) => void
  /** v9. Put the question back in the page's box to reword it. */
  onEdit?: (question: string) => void
  question: string
  /** Null while One is reading. */
  answer: OneAnswer | null
  asking: boolean
  /**
   * The page's "Post to the board" button (23 Sep). A counter, not a flag:
   * every press is a request, and it is compared against the value this
   * mount started with, so a fresh panel never opens the route on its own.
   * The route opens HERE because `useRouting` lives here — the page sends
   * nothing.
   */
  postRequest?: number
  /** A question went up on the board — the list under the box should re-read
   *  so it appears there. (Your todo: "wiki one does not refresh when a
   *  question is posted".) */
  onPosted?: () => void
}) {
  const c = ASK[useHubLang()]
  const openLanes = useOpenLanes()
  const route = useRouting(openLanes)

  const live = route.outcome?.kind === 'board' && route.outcome.post.state === 'live'
  useEffect(() => {
    if (live) onPosted?.()
    // Fires once, on the transition to a live post; onPosted is a fresh
    // closure every render and must not re-trigger the read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live])

  /* The board door, opened from the page's button: BoardStep, with 034's
     warning read at the moment of choosing. A second press while it is open
     changes nothing, so nothing typed is lost. One's own words go in as
     background only when they are an answer; an "unavailable" message is not
     something anybody should post. */
  const seenPost = useRef(postRequest)
  const routeRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (postRequest === seenPost.current) return
    seenPost.current = postRequest
    if (!answer) return
    if (route.phase !== 'routing') {
      if (route.phase === 'sent') route.reset()
      const usable = !answer.unavailable && !answer.rateLimited && answer.found
      route.openBoard(question, usable ? answer.answer : '', answer.logRef ?? null)
    }
    requestAnimationFrame(() =>
      routeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postRequest])

  if (asking || !answer) {
    return (
      <div className="wiki-ask">
        <div className="onemsg onemsg--one onemsg--waiting" aria-live="polite" aria-busy="true">
          <p className="onemsg__label">
            <span className="onemsg__mark onemsg__mark--pulse" aria-hidden="true">
              <ConeTecMark size={14} />
            </span>
            {c.reading}
          </p>
          <div className="onemsg__skeleton" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    )
  }

  const usable = !answer.unavailable && !answer.rateLimited
  /* v9.5. An idea or a fault One recognised: the same LINK the panel shows. */
  const kind = usable && answer.request ? answer.request : null

  return (
    <div className="wiki-ask" aria-live="polite">
      <OneMessage message={{ who: 'one', answer }} onPick={onAsk} onEdit={onEdit} />

      {route.phase === 'idle' && usable && answer.found && (
        <Thumbs one={answer} from={answer.logRef ?? null} />
      )}
      {route.phase === 'idle' && kind && (
        <RequestLink kind={kind} question={question} from={answer.logRef ?? null} />
      )}

      {/* No answer at all: the door, prefilled with the question - the same
          rule as the panel, and the same link. */}
      {route.phase === 'idle' && usable && !kind && !answer.found && !answer.clarify?.length && (
        <div className="onerate">
          <HelpDoor question={question} from={answer.logRef ?? null} />
        </div>
      )}

      {route.phase === 'routing' && (
        <div ref={routeRef}>
          <BoardStep
            question={route.question}
            setQuestion={route.setQuestion}
            detail={route.detail}
            setDetail={route.setDetail}
            onSend={() => void route.post()}
            onCancel={route.cancel}
            busy={route.posting}
          />
        </div>
      )}

      {route.phase === 'sent' && route.outcome && (
        <RouteSent outcome={route.outcome} onDone={route.reset} />
      )}

      {route.error && (
        <p className="onedrawer__error" role="alert">
          {route.error}
        </p>
      )}
    </div>
  )
}
